'use client'

import React, { createContext, ReactNode, useCallback, useContext, useState } from 'react'

interface SearchResetContextType {
  resetFilters: (() => void) | null
  registerResetFunction: (resetFn: () => void) => void
  unregisterResetFunction: () => void
}

const SearchResetContext = createContext<SearchResetContextType>({
  resetFilters: null,
  registerResetFunction: () => {},
  unregisterResetFunction: () => {}
})

interface SearchResetProviderProps {
  children: ReactNode
}

/**
 * Provider for search reset functionality across the application.
 * Allows components to register/unregister reset functions dynamically.
 *
 * @param children - The child components to wrap
 * @returns SearchResetProvider component
 */
export const SearchResetProvider: React.FC<SearchResetProviderProps> = ({ children }) => {
  const [resetFilters, setResetFilters] = useState<(() => void) | null>(null)

  const registerResetFunction = useCallback((resetFn: () => void) => {
    setResetFilters(() => resetFn)
  }, [])

  const unregisterResetFunction = useCallback(() => {
    setResetFilters(null)
  }, [])

  return (
    <SearchResetContext.Provider
      value={{ resetFilters, registerResetFunction, unregisterResetFunction }}>
      {children}
    </SearchResetContext.Provider>
  )
}

/**
 * Hook to access search reset functionality.
 * Returns null for resetFilters when used outside SearchResetProvider.
 *
 * @returns The search reset context with resetFilters function or null
 */
export const useSearchReset = (): SearchResetContextType => {
  const context = useContext(SearchResetContext)
  if (context === undefined) {
    return {
      resetFilters: null,
      registerResetFunction: () => {},
      unregisterResetFunction: () => {}
    }
  }
  return context
}
